import React, { useState, useEffect, useRef } from "react";
import { Snackbar, Alert } from "@mui/material";
import { auth, provider, db, isFirebaseConfigured } from "../firebase";
import { signInWithPopup, signOut } from "firebase/auth";
import { collection, addDoc, onSnapshot } from "firebase/firestore";

const VOTE_IMAGES = [
  { id: 0, src: "/logoVoting/logo1.jpg", label: "Logo 1" },
  { id: 1, src: "/logoVoting/logo2.jpg", label: "Logo 2" },
  { id: 2, src: "/logoVoting/logo3.jpg", label: "Logo 3" },
  { id: 3, src: "/logoVoting/logo4.jpg", label: "Logo 4" },
  { id: 4, src: "/logoVoting/logo5.png", label: "Logo 5" },
];

export default function LogoVoting() {
  // ── Auth ──
  const [user, setUser] = useState(null);

  // ── Firestore votes (real‑time) ──
  const [votes, setVotes] = useState([]);

  // ── UI state ──
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState(null);
  const [detailImage, setDetailImage] = useState(null);
  const [imageSizes, setImageSizes] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const sizeCacheRef = useRef({});
  const firebaseUnavailable = !isFirebaseConfigured;

  // ── Login / logout ──
  const login = async () => {
    try {
      if (!auth) {
        setError("Voting is unavailable right now.");
        return;
      }
      const result = await signInWithPopup(auth, provider);
      setUser({
        email: result.user.email,
        photo: result.user.photoURL,
        displayName: result.user.displayName,
      });
    } catch (err) {
      console.error("Login failed:", err);
      setError("Google login failed – try again.");
    }
  };
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setSelectedImage(null);
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  // ── Firestore listener ──
  useEffect(() => {
    if (!db) return undefined;

    const unsub = onSnapshot(
      collection(db, "votes"),
      (snap) => {
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setVotes(data);
      },
      (err) => {
        console.error("Firestore snapshot error:", err);
        setError("Unable to load voting data.");
      },
    );
    return () => unsub();
  }, []);

  // ── Image file‑size fetch (optional) ──
  useEffect(() => {
    VOTE_IMAGES.forEach((img) => {
      fetch(img.src, { method: "HEAD" })
        .then((res) => {
          const len = res.headers.get("content-length");
          if (len) {
            const kb = (parseInt(len, 10) / 1024).toFixed(1);
            sizeCacheRef.current[img.id] = kb;
            setImageSizes((p) => ({ ...p, [img.id]: kb }));
          }
        })
        .catch(() => {
          sizeCacheRef.current[img.id] = null;
          setImageSizes((p) => ({ ...p, [img.id]: null }));
        });
    });
  }, []);

  // ── Auto‑dismiss alerts ──
  useEffect(() => {
    if (isSuccess || error) {
      const t = setTimeout(() => {
        setIsSuccess(false);
        setError(null);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [isSuccess, error]);

  // ── Helpers ──
  const voteCount = (id) => votes.filter((v) => v.logoId === id).length;
  const votersFor = (id) =>
    votes.filter((v) => v.logoId === id).map((v) => v.name);
  const alreadyVoted = (email) =>
    votes.some((v) => v.email?.toLowerCase() === email?.toLowerCase());

  const handleCheckboxChange = (id) => {
    setSelectedImage((prev) => (prev === id ? null : id));
  };
  const handleImageClick = (img) => setDetailImage(img);
  const closeDetail = () => setDetailImage(null);

  // ── Submit vote ──
  const handleSubmit = async () => {
    if (!user || !db) {
      setError("You must be signed in to vote.");
      return;
    }
    if (alreadyVoted(user.email)) {
      setError("You have already voted.");
      return;
    }
    if (selectedImage === null) {
      setError("Please select a logo.");
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, "votes"), {
        name: user.displayName || user.email,
        email: user.email,
        logoId: selectedImage,
        logoLabel: VOTE_IMAGES[selectedImage]?.label ?? "",
        votedAt: new Date().toISOString(),
      });
      setIsSuccess(true);
      setSuccessMessage("Your vote was recorded – thank you! 🎉");
      setSelectedImage(null);
    } catch (err) {
      console.error("Vote submit error:", err);
      setError("Failed to record vote – try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Not signed in ──
  if (!user) {
    if (firebaseUnavailable) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center max-w-sm w-full">
            <div className="text-5xl mb-4">Voting paused</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Zayverse Logo Voting
            </h1>
            <p className="text-gray-500 mb-4 text-sm">
              Firebase configuration is missing in this deployment, so voting is
              disabled for now.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 text-center max-w-sm w-full">
          <div className="text-5xl mb-4">🗳️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Zayverse Logo Voting
          </h1>
          <p className="text-gray-500 mb-4 text-sm">
            Sign in with Google to cast your vote
          </p>
          <button
            onClick={login}
            className="w-full flex items-center justify-center gap-2 bg-white border-2 border-gray-200 hover:border-blue-400 hover:shadow-md rounded-xl px-4 py-2 text-sm font-semibold text-gray-700 transition-all"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Sign in with Google
          </button>
        </div>

        {/* Error snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={3000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setError(null)}
            sx={{
              width: "100%",
              maxWidth: "360px",
              fontSize: "14px",
              paddingY: "20px",
              borderRadius: "8px",
              backgroundColor: "rgba(255,191,205,0.708)",
              backdropFilter: "blur(3px)",
              border: "1px solid gray",
              color: "#1d2951",
              "& .MuiAlert-icon": { color: "#ff0000" },
            }}
          >
            {error}
          </Alert>
        </Snackbar>
      </div>
    );
  }

  // ── Signed‑in UI ──
  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 md:px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header – collapses on mobile */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-3 md:gap-0">
          <h1 className="text-2xl font-bold text-gray-900">
            🗳️ Zayverse Logo Voting
          </h1>
          <div className="flex items-center gap-3">
            {user.photo && (
              <img
                src={user.photo}
                alt="avatar"
                className="w-8 h-8 rounded-full"
              />
            )}
            <span className="text-sm text-gray-600">{user.email}</span>
            <button
              onClick={logout}
              className="text-xs text-red-500 hover:text-red-700 font-semibold border border-red-300 rounded-lg px-3 py-1.5 hover:bg-red-50 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Already‑voted banner */}
        {alreadyVoted(user.email) && (
          <div className="bg-green-50 border border-green-300 rounded-xl p-4 mb-6 text-center">
            <p className="text-green-700 font-semibold text-sm">
              ✅ You have already voted – thank you!
            </p>
          </div>
        )}

        {/* Success snackbar */}
        <Snackbar
          open={isSuccess}
          autoHideDuration={3000}
          onClose={() => setIsSuccess(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setIsSuccess(false)}
            sx={{
              width: "100%",
              maxWidth: "360px",
              fontSize: "14px",
              paddingY: "20px",
              borderRadius: "8px",
              backgroundColor: "rgba(144,251,132,0.583)",
              backdropFilter: "blur(3px)",
              border: "1px solid #778a7e",
              color: "#1d2951",
            }}
          >
            {successMessage || "Vote recorded successfully!"}
          </Alert>
        </Snackbar>

        {/* Error snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={3000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setError(null)}
            sx={{
              width: "100%",
              maxWidth: "360px",
              fontSize: "14px",
              paddingY: "20px",
              borderRadius: "8px",
              backgroundColor: "rgba(255,191,205,0.708)",
              backdropFilter: "blur(3px)",
              border: "1px solid gray",
              color: "#1d2951",
              "& .MuiAlert-icon": { color: "#ff0000" },
            }}
          >
            {error}
          </Alert>
        </Snackbar>

        {/* Image grid */}
        <div className="bg-white rounded-xl shadow-md p-4 md:p-6 mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Click an image to view details, then tick the box to select:
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {VOTE_IMAGES.map((img) => (
              <div key={img.id} className="flex flex-col items-center">
                {/* Thumbnail */}
                <div
                  onClick={() => handleImageClick(img)}
                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 w-full ${
                    selectedImage === img.id
                      ? "border-blue-500 ring-2 ring-blue-400 shadow-lg"
                      : voteCount(img.id) > 0
                        ? "border-green-500 ring-2 ring-green-400 shadow-lg"
                        : "border-gray-200 hover:border-blue-300"
                  }`}
                >
                  <img
                    src={img.src}
                    alt={img.label}
                    className="w-full h-32 sm:h-36 md:h-40 object-contain"
                  />
                  {/* Vote badge */}
                  {voteCount(img.id) > 0 && (
                    <div className="absolute top-1 right-1 bg-green-500 rounded-full min-w-[24px] h-6 px-1.5 flex items-center justify-center shadow-md">
                      <span className="text-white text-xs font-bold">
                        {voteCount(img.id)}
                      </span>
                    </div>
                  )}
                  {/* Selected checkmark (only when no votes yet) */}
                  {selectedImage === img.id && voteCount(img.id) === 0 && (
                    <div className="absolute top-1 right-1 bg-blue-500 rounded-full w-6 h-6 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center py-1">
                    {img.label}
                  </div>
                </div>

                {/* Checkbox */}
                <label className="mt-2 flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={selectedImage === img.id}
                    onChange={() => handleCheckboxChange(img.id)}
                    disabled={alreadyVoted(user.email)}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-600">Select</span>
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Submit button (no name selector) */}
        <div className="flex items-center gap-3 bg-white rounded-xl shadow-md p-4 md:p-6 mb-6">
          <button
            onClick={handleSubmit}
            disabled={alreadyVoted(user.email) || submitting}
            className={`flex-1 font-semibold py-2 md:py-3 px-4 md:px-6 rounded-lg transition-colors duration-200 shadow-md ${
              alreadyVoted(user.email) || submitting
                ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {submitting
              ? "Submitting..."
              : alreadyVoted(user.email)
                ? "Already Voted"
                : "Submit Vote"}
          </button>
        </div>

        {/* ── Vote history ── */}
        <div
          id="vote-history"
          className="mt-8 bg-white rounded-xl shadow-md p-4 md:p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">
              Vote History ({votes.length} votes)
            </h2>
          </div>

          {votes.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">
              No votes yet. Be the first to vote!
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {VOTE_IMAGES.map((img) => {
                const count = voteCount(img.id);
                const voterNames = votersFor(img.id);
                if (count === 0) return null;

                return (
                  <div
                    key={img.id}
                    /*  ── RESPONSIVE ROW ──
                • Mobile (default)   → column layout, height auto
                • md and up          → traditional row layout
            */
                    className="
              flex flex-col md:flex-row
              items-start md:items-center
              gap-3 w-full
              bg-blue-50 rounded-lg border border-blue-300
              p-3
            "
                  >
                    {/* ── Logo thumbnail ── */}
                    <img
                      src={img.src}
                      alt={img.label}
                      className="w-14 h-14 rounded-md object-contain border border-blue-300 flex-shrink-0"
                    />

                    {/* ── Details container (label + names) ── */}
                    <div className="flex flex-col flex-1 w-full">
                      {/* Logo label */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-800">
                          {img.label}
                        </span>
                        {/* optional separator for visual consistency */}
                        <span className="text-gray-400 hidden md:inline">
                          :
                        </span>
                      </div>

                      {/* Voter names */}
                      <div className="mt-1 text-sm text-gray-700 break-words">
                        {/* Mobile – each name on its own line */}
                        <div className="md:hidden flex flex-col gap-0.5">
                          {voterNames.map((name, i) => (
                            <span key={i} className="font-medium text-gray-800">
                              {name}
                            </span>
                          ))}
                        </div>

                        {/* Desktop – comma‑separated (hidden on mobile) */}
                        <p className="hidden md:block truncate">
                          {voterNames.map((name, i) => (
                            <span key={i}>
                              <span className="font-medium text-gray-800">
                                {name}
                              </span>
                              {i < voterNames.length - 1 && (
                                <span className="text-gray-400">, </span>
                              )}
                            </span>
                          ))}
                        </p>
                      </div>
                    </div>

                    {/* ── Vote count badge (always stays on the right) ── */}
                    <div className="flex-shrink-0 bg-green-500 rounded-full min-w-[28px] h-7 px-2 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">
                        {count}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Detail modal – mobile width */}
      {detailImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
          onClick={closeDetail}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md md:max-w-lg mx-4 p-6 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeDetail}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <img
              src={detailImage.src}
              alt={detailImage.label}
              className="w-full max-h-[60vh] object-contain rounded-lg mb-4"
            />

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {detailImage.label}
                </h3>
                {imageSizes[detailImage.id] ? (
                  <p className="text-sm text-gray-500">
                    File size: {imageSizes[detailImage.id]} KB
                  </p>
                ) : (
                  <p className="text-sm text-gray-400">File size: loading…</p>
                )}
              </div>

              <button
                onClick={() => {
                  handleCheckboxChange(detailImage.id);
                  closeDetail();
                }}
                disabled={alreadyVoted(user.email)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors duration-200 ${
                  alreadyVoted(user.email)
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : selectedImage === detailImage.id
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-blue-100"
                }`}
              >
                {selectedImage === detailImage.id
                  ? "✓ Selected"
                  : "Select This Logo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
